import { ADJACENCY, MAPS, SPACE_IDS } from '../maps/index.js';
export * from './types.js';
const RANKS = [1, 2, 3, 4];
const EMPTY_ENTITLEMENTS = { placeRemaining: 0, mayPay: false, mayMove: false, mayCapture: false };
const freshBank = () => ({ 1: 4, 2: 3, 3: 2, 4: 1 });
const emptyBoard = () => Object.fromEntries(SPACE_IDS.map((id) => [id, []]));
const other = (index) => index === 0 ? 1 : 0;
const copyState = (value) => JSON.parse(JSON.stringify(value));
function player(side, name) {
    return { side, name, bank: freshBank(), hand: [] };
}
export function createGame(mapId, names, goldIndex) {
    const goldName = names[goldIndex];
    const silverName = names[other(goldIndex)];
    return {
        mapId,
        players: [player('gold', goldName), player('silver', silverName)],
        board: emptyBoard(),
        current: 0,
        phase: 'awaitFlip',
        entitlements: { ...EMPTY_ENTITLEMENTS },
        turnNumber: 1,
        postFlipSnapshot: null,
        actionsThisTurn: [],
        result: null,
        seq: 0,
    };
}
export function entitlementsFor(matches) {
    if (matches === 4)
        return { placeRemaining: 2, mayPay: true, mayMove: false, mayCapture: false };
    if (matches >= 2)
        return { placeRemaining: 2, mayPay: false, mayMove: false, mayCapture: false };
    if (matches === 1)
        return { placeRemaining: 1, mayPay: false, mayMove: true, mayCapture: false };
    return { placeRemaining: 0, mayPay: false, mayMove: true, mayCapture: true };
}
export function startTurnFlip(state, rng) {
    if (state.phase !== 'awaitFlip')
        throw new Error('A flip is only valid at the start of a turn.');
    const next = copyState(state);
    const active = next.players[next.current];
    if (active.hand.length !== 0)
        throw new Error('Hand must be empty before the turn flip.');
    for (const rank of RANKS) {
        if (active.bank[rank] > 0) {
            active.bank[rank] -= 1;
            active.hand.push({
                rank,
                flipped: rng() < 0.5 ? 'gold' : 'silver',
                uid: `coin-${next.seq + 1}-${next.turnNumber}-${next.current}-${rank}`,
            });
        }
    }
    if (active.hand.length === 0)
        throw new Error('A live player cannot start a turn with an empty bank.');
    const matches = active.hand.filter((coin) => coin.flipped === active.side).length;
    next.entitlements = entitlementsFor(matches);
    next.phase = 'actions';
    next.actionsThisTurn = [];
    next.seq += 1;
    const snapshot = copyState(next);
    snapshot.postFlipSnapshot = null;
    next.postFlipSnapshot = snapshot;
    return {
        state: next,
        events: [
            { type: 'flipStarted', player: next.current },
            { type: 'flipResult', coins: copyState(active.hand), matches },
        ],
    };
}
export function legal(state) {
    const active = state.players[state.current];
    const inActions = state.phase === 'actions';
    const matchedRanks = active.hand
        .filter((coin) => coin.flipped === active.side)
        .map((coin) => coin.rank);
    const placeableRanks = inActions && state.entitlements.placeRemaining > 0 ? matchedRanks : [];
    const payableRanks = inActions && state.entitlements.mayPay ? matchedRanks : [];
    const placeTargets = (rank) => {
        if (!placeableRanks.includes(rank))
            return [];
        return SPACE_IDS.filter((id) => {
            const stack = state.board[id];
            const top = stack.at(-1);
            return top === undefined || top.rank > rank;
        });
    };
    const moveTargets = (from) => {
        if (!inActions || !state.entitlements.mayMove || state.board[from].length === 0)
            return [];
        return ADJACENCY[from].filter((id) => state.board[id].length === 0);
    };
    const moveSources = inActions && state.entitlements.mayMove
        ? SPACE_IDS.filter((id) => moveTargets(id).length > 0)
        : [];
    const captureTargets = inActions && state.entitlements.mayCapture
        ? SPACE_IDS.filter((id) => state.board[id].length > 0)
        : [];
    return {
        placeableRanks: [...new Set(placeableRanks)],
        placeTargets,
        payableRanks: [...new Set(payableRanks)],
        moveSources,
        moveTargets,
        captureTargets,
        canUndo: inActions && state.actionsThisTurn.length > 0 && state.postFlipSnapshot !== null,
        canEndTurn: inActions,
    };
}
const ruleError = (code, message) => ({ error: { code, message } });
function foldHandsIntoBanks(state) {
    for (const p of state.players) {
        for (const coin of p.hand)
            p.bank[coin.rank] += 1;
        p.hand = [];
    }
}
function winnerFromScore(breakdown) {
    if (breakdown.total[0] !== breakdown.total[1])
        return breakdown.total[0] > breakdown.total[1] ? 0 : 1;
    if (breakdown.bankValue[0] !== breakdown.bankValue[1])
        return breakdown.bankValue[0] > breakdown.bankValue[1] ? 0 : 1;
    return null;
}
function finalize(state, reason, forcedWinner) {
    foldHandsIntoBanks(state);
    state.phase = 'gameOver';
    state.entitlements = { ...EMPTY_ENTITLEMENTS };
    state.postFlipSnapshot = null;
    const breakdown = score(state);
    const result = { reason, winner: forcedWinner ?? winnerFromScore(breakdown), breakdown };
    state.result = result;
    return { type: 'gameOver', result };
}
function immediateEndReason(state) {
    if (SPACE_IDS.every((id) => state.board[id].length > 0))
        return 'mapFull';
    const active = state.players[state.current];
    const bankCount = RANKS.reduce((sum, rank) => sum + active.bank[rank], 0);
    if (bankCount === 0 && active.hand.length === 0)
        return 'coinsExhausted';
    return null;
}
export function apply(state, action) {
    if (state.phase !== 'actions')
        return ruleError('WRONG_PHASE', 'Actions are only allowed after the flip.');
    const legality = legal(state);
    const next = copyState(state);
    const active = next.players[next.current];
    let event;
    switch (action.type) {
        case 'place': {
            if (!legality.placeableRanks.includes(action.rank) || !legality.placeTargets(action.rank).includes(action.spaceId)) {
                return ruleError('ILLEGAL_PLACE', 'That coin cannot be placed there.');
            }
            const coinIndex = active.hand.findIndex((coin) => coin.rank === action.rank && coin.flipped === active.side);
            const [coin] = active.hand.splice(coinIndex, 1);
            if (!coin)
                return ruleError('MISSING_COIN', 'The selected coin is no longer in hand.');
            next.board[action.spaceId].push({ rank: coin.rank, side: active.side, uid: coin.uid });
            next.entitlements.placeRemaining -= 1;
            event = { type: 'placed', player: next.current, rank: action.rank, spaceId: action.spaceId, uid: coin.uid };
            break;
        }
        case 'pay': {
            if (!legality.payableRanks.includes(action.rank))
                return ruleError('ILLEGAL_PAY', 'That coin cannot be paid.');
            const coinIndex = active.hand.findIndex((coin) => coin.rank === action.rank && coin.flipped === active.side);
            const [coin] = active.hand.splice(coinIndex, 1);
            if (!coin)
                return ruleError('MISSING_COIN', 'The selected coin is no longer in hand.');
            next.players[other(next.current)].bank[action.rank] += 1;
            next.entitlements.mayPay = false;
            next.entitlements.placeRemaining += 1;
            event = { type: 'paid', player: next.current, rank: action.rank, uid: coin.uid };
            break;
        }
        case 'move': {
            if (!legality.moveSources.includes(action.from) || !legality.moveTargets(action.from).includes(action.to)) {
                return ruleError('ILLEGAL_MOVE', 'That stack cannot move there.');
            }
            next.board[action.to] = next.board[action.from];
            next.board[action.from] = [];
            next.entitlements.mayMove = false;
            event = { type: 'moved', player: next.current, from: action.from, to: action.to };
            break;
        }
        case 'capture': {
            if (!legality.captureTargets.includes(action.spaceId))
                return ruleError('ILLEGAL_CAPTURE', 'There is no capturable coin there.');
            const coin = next.board[action.spaceId].pop();
            if (!coin)
                return ruleError('MISSING_COIN', 'The coin is no longer on the board.');
            active.bank[coin.rank] += 1;
            next.entitlements.mayCapture = false;
            event = { type: 'captured', player: next.current, spaceId: action.spaceId, coin };
            break;
        }
    }
    next.actionsThisTurn.push(action);
    next.seq += 1;
    const events = [event];
    const endReason = immediateEndReason(next);
    if (endReason)
        events.push(finalize(next, endReason));
    return { state: next, events };
}
export function undoTurn(state) {
    const legality = legal(state);
    if (!legality.canUndo || !state.postFlipSnapshot)
        throw new Error('There are no turn actions to undo.');
    const base = copyState(state.postFlipSnapshot);
    base.postFlipSnapshot = null;
    const restored = copyState(base);
    restored.postFlipSnapshot = base;
    restored.seq = state.seq + 1;
    return { state: restored, events: [{ type: 'undone', player: restored.current }] };
}
export function endTurn(state) {
    if (!legal(state).canEndTurn)
        throw new Error('The turn cannot end right now.');
    const next = copyState(state);
    const previous = next.current;
    const active = next.players[previous];
    for (const coin of active.hand)
        active.bank[coin.rank] += 1;
    active.hand = [];
    next.current = other(previous);
    next.turnNumber += 1;
    next.phase = 'awaitFlip';
    next.entitlements = { ...EMPTY_ENTITLEMENTS };
    next.actionsThisTurn = [];
    next.postFlipSnapshot = null;
    next.seq += 1;
    return { state: next, events: [{ type: 'turnEnded', player: previous }, { type: 'turnStarted', player: next.current }] };
}
export function resign(state, byIndex) {
    if (state.phase === 'gameOver')
        throw new Error('The game is already over.');
    const next = copyState(state);
    next.seq += 1;
    const event = finalize(next, 'resigned', other(byIndex));
    return { state: next, events: [event] };
}
export function claimVictory(state, winner) {
    if (state.phase === 'gameOver')
        throw new Error('The game is already over.');
    const next = copyState(state);
    next.seq += 1;
    const event = finalize(next, 'timeout-claim', winner);
    return { state: next, events: [event] };
}
export function score(state) {
    const map = MAPS[state.mapId];
    const spaceControl = [0, 0];
    const regionControl = new Map();
    const regionVP = new Map();
    for (const region of map.regions) {
        regionControl.set(region.id, [0, 0]);
        regionVP.set(region.id, [0, 0]);
    }
    for (const space of map.spaces) {
        const top = state.board[space.id].at(-1);
        if (!top)
            continue;
        const owner = top.side === 'gold' ? 0 : 1;
        spaceControl[owner] += top.rank;
        regionControl.get(space.regionId)[owner] += 1;
        regionVP.get(space.regionId)[owner] += top.rank;
    }
    const regions = map.regions.map((region) => {
        const controlledSpaces = regionControl.get(region.id);
        const bonusTo = controlledSpaces[0] === controlledSpaces[1]
            ? null
            : controlledSpaces[0] > controlledSpaces[1] ? 0 : 1;
        return {
            regionId: region.id,
            controlledSpaces: [...controlledSpaces],
            bonusTo,
            bonusVP: bonusTo === null ? 0 : regionVP.get(region.id)[bonusTo],
        };
    });
    const bankBonus = [0, 0];
    const bankValue = [0, 0];
    for (const index of [0, 1]) {
        for (const rank of RANKS) {
            bankBonus[index] += state.players[index].bank[rank];
            bankValue[index] += state.players[index].bank[rank] * rank;
        }
    }
    const total = [...spaceControl];
    for (const index of [0, 1])
        total[index] += bankBonus[index];
    for (const region of regions)
        if (region.bonusTo !== null)
            total[region.bonusTo] += region.bonusVP;
    return { spaceControl, regions, bankBonus, bankValue, total };
}
export function describeState(state, viewerIndex) {
    if (state.phase === 'gameOver') {
        if (state.result?.winner === null)
            return 'The game is a draw!';
        return `${state.players[state.result.winner].name} wins!`;
    }
    const active = state.players[state.current];
    if (viewerIndex !== undefined && viewerIndex !== state.current)
        return `Waiting for ${active.name}…`;
    if (state.phase === 'awaitFlip')
        return `${active.name}, flip your coins!`;
    const hand = active.hand;
    const matches = hand.filter((coin) => coin.flipped === active.side).length;
    const legality = legal(state);
    if (state.actionsThisTurn.length === 0 && matches === 0)
        return 'No matches — capture a coin and move a stack (any order).';
    if (matches === 1 && legality.placeableRanks.length > 0) {
        return state.entitlements.mayMove
            ? '1 match — place 1 coin and move a stack (any order).'
            : '1 match — place 1 coin.';
    }
    if (legality.placeableRanks.length > 0)
        return `${matches} ${matches === 1 ? 'match' : 'matches'} — place up to ${state.entitlements.placeRemaining} ${state.entitlements.placeRemaining === 1 ? 'coin' : 'coins'}.`;
    if (legality.captureTargets.length > 0)
        return 'Choose any occupied space to capture its top coin.';
    if (legality.moveSources.length > 0)
        return 'Choose a stack to move.';
    return 'Out of actions — end your turn.';
}
export function totalCoins(state) {
    let total = 0;
    for (const p of state.players) {
        total += p.hand.length;
        for (const rank of RANKS)
            total += p.bank[rank];
    }
    for (const id of SPACE_IDS)
        total += state.board[id].length;
    return total;
}
//# sourceMappingURL=index.js.map